import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Mail, Send } from "lucide-react";

export default function EmailIntakeSimulatorPage() {
  const [folders, setFolders] = useState([]);
  const [scanEmail, setScanEmail] = useState("");
  const [sender, setSender] = useState("scanner@office.example");
  const [subject, setSubject] = useState("Scanned document");
  const [file, setFile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = async () => {
    const [f, l] = await Promise.all([api.get("/folders"), api.get("/email-intake/logs").catch(() => ({ data: [] }))]);
    setFolders(f.data);
    setLogs(l.data || []);
    if (!scanEmail && f.data[0]) setScanEmail(f.data[0].scan_email);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");
    if (!file) {
      setErr("Choose a file.");
      return;
    }
    const fd = new FormData();
    fd.append("scan_email", scanEmail);
    fd.append("sender_email", sender);
    fd.append("subject", subject);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/email-intake/simulate", fd);
      setOk("Email intake simulated. Document queued for OCR.");
      setFile(null);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Email Intake Simulator">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="ds-card p-5" data-testid="email-simulate-form">
          <div className="ds-overline">Inbound email</div>
          <h3 className="font-display text-lg font-semibold tracking-tight mb-4">
            Simulate scan-to-email
          </h3>
          <div className="space-y-3">
            <div>
              <label className="ds-label">Folder scan address</label>
              <select className="ds-input" value={scanEmail} onChange={(e) => setScanEmail(e.target.value)} data-testid="scan-email-select">
                {folders.map((f) => <option key={f.id} value={f.scan_email}>{f.name} — {f.scan_email}</option>)}
              </select>
            </div>
            <div>
              <label className="ds-label">Sender</label>
              <input className="ds-input" value={sender} onChange={(e) => setSender(e.target.value)} data-testid="sender-input" />
            </div>
            <div>
              <label className="ds-label">Subject</label>
              <input className="ds-input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="subject-input" />
            </div>
            <div>
              <label className="ds-label">Attachment</label>
              <input className="ds-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.docx,.xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="email-file-input" />
            </div>
            {err && <div className="text-[12px] text-[#B42318]">{err}</div>}
            {ok && <div className="text-[12px] text-[#027A48]">{ok}</div>}
            <button className="ds-btn-primary flex items-center gap-2" disabled={busy} data-testid="email-simulate-submit">
              <Send size={13} /> {busy ? "Sending…" : "Simulate intake"}
            </button>
          </div>
        </form>

        <div className="ds-card p-5">
          <div className="ds-overline">Recent intakes</div>
          <h3 className="font-display text-lg font-semibold tracking-tight mb-3">Email log</h3>
          <ul className="space-y-2">
            {logs.slice(0, 20).map((l) => (
              <li key={l.id} className="border border-[#E5E5E9] p-3 text-[12px]">
                <div className="flex items-center gap-2 font-mono">
                  <Mail size={12} /> {l.sender_email} → {l.scan_email}
                </div>
                <div className="mt-0.5">{l.subject}</div>
                <div className="ds-overline mt-1" style={{ fontSize: 10 }}>
                  {new Date(l.received_at).toLocaleString()}
                </div>
              </li>
            ))}
            {logs.length === 0 && <li className="text-[12px] text-[#8A8A93]">No intakes yet.</li>}
          </ul>
        </div>
      </div>
    </AppShell>
  );
}
